<template>
  <div>
    <div class="d-flex button-group"
         :class="{'justify-content-between': !isNewInvoice,
                    'justify-content-end': isNewInvoice}">
      <div class="button-group margin-0 d-flex" v-if="!isNewInvoice">
        <history-modal/>
        <b-btn size="sm" class="history-btn" @click="openHistoryModal">
          {{$t('buttons.common.history')}}
        </b-btn>
        <b-popover target="print-invoice" :show.sync="popoverPrintWithStatus" placement="bottom"
                   ref="print-invoice-popover">
          <div>
            <b-btn size="sm" class="full-width" @click="printInvoice(null)">
              {{$t('labels.invoice.wo_grouping')}}
            </b-btn>
          </div>
          <div class="mt-2">
            <b-btn size="sm" class="full-width" @click="printInvoice('TxWxPRICExSO')">
              {{$t('labels.invoice.by_order_price_txw')}}
            </b-btn>
          </div>
          <div class="mt-2">
            <b-btn size="sm" class="full-width" @click="printInvoice('PRICE')">
              {{$t('labels.invoice.by_price')}}
            </b-btn>
          </div>
        </b-popover>
        <b-btn variant="outline-secondary" id="print-invoice" :title="$t('labels.invoice.print_invoice')"
               class="d-inline-flex" size="sm">
          <icon name="print"/>
        </b-btn>
        <documents-templates-modal @afterDownload="fetchDocuments"
          :file-upload-url="documentsTemplateNameUploadUrl" :template-type="documentsTemplateType"/>
        <b-popover target="select-template-type" placement="bottom"
                   ref="select-template-type-popover" :show.sync="popoverDocumentTemplateType">
          <div>
            <b-btn size="sm" class="full-width" @click="openTemplatesModal('invoice')">
              {{$t('labels.invoice.invoice')}}
            </b-btn>
          </div>
          <div class="mt-2">
            <b-btn size="sm" class="full-width" @click="openTemplatesModal('packing_list')">
              {{$t('labels.invoice.packing_list')}}
            </b-btn>
          </div>
        </b-popover>
        <b-btn variant="secondary" id="select-template-type" :title="$t('labels.invoice.generate_packing_list')"
               class="d-inline-flex" size="sm">
          <icon name="list-alt"/>
        </b-btn>
        <b-btn :title="$t('labels.invoice.generate_ctb')" class="d-inline-flex"
               @click="generateCTB" size="sm">
          {{$t('buttons.sales_invoices.ctb')}}
        </b-btn>
      </div>
      <div class="button-group margin-0 d-flex">
        <b-btn variant="secondary" class="d-inline-flex" @click="generateDocs" size="sm">
          {{$t('buttons.common.generate_docs')}}
        </b-btn>
        <b-btn v-if="!isNewInvoice && !isApprovedInvoice" variant="success" :title="billTitle" class="d-inline-flex"
               @click="changeSalesInvoiceStatus('move_status_forward')" size="sm">
          {{$t('buttons.common.bill')}}
        </b-btn>
        <b-btn v-if="!isNewInvoice && !isApprovedInvoice" variant="success" :title="billTitle" class="d-inline-flex"
               @click="changeSalesInvoiceStatus('move_status_forward', true)" size="sm">
          {{$t('buttons.common.bill_and_post')}}
        </b-btn>
        <b-btn v-if="!isNewInvoice && isApprovedInvoice" variant="primary" class="d-inline-flex"
               @click="changeSalesInvoiceStatus('move_status_back')" size="sm">
          {{$t('buttons.common.roll_back')}}
        </b-btn>
        <b-btn variant="secondary" class="d-inline-flex" @click="save" size="sm">
          {{$t('buttons.common.save')}}
        </b-btn>
        <b-btn v-if="!isApprovedInvoice && !isNewInvoice" variant="outline-danger" class="d-inline-flex"
               @click="deleteSalesInvoice" size="sm">
          {{$t('buttons.common.delete')}}
        </b-btn>
      </div>
    </div>
    <div class="d-flex justify-content-between toggle-container"
         @click="showCollapse = !showCollapse"
         :aria-expanded="showCollapse"
         aria-controls="collapse">
      <h3 class="m-0" v-if="invoiceData.main_related_invoice && !isNewInvoice && isGeneralType">
        {{invoiceTitle}} / {{invoiceData.invoice_number}} /
        <router-link @click.native="$event.stopImmediatePropagation()"
                     :to="`/sales-invoice/${invoiceData.main_related_invoice.id}`">
          {{invoiceData.main_related_invoice.invoice_number}}
        </router-link>
        -

        {{invoiceStatus}}
      </h3>
      <h3 class="m-0" v-if="invoiceData.main_related_invoice && !isNewInvoice && (isCommissionType || isServiceType)">
        {{invoiceTitle}} /
        <router-link @click.native="$event.stopImmediatePropagation()"
                     :to="`/sales-invoice/${invoiceData.main_related_invoice.id}`">
          {{invoiceData.main_related_invoice.invoice_number}}
        </router-link>
        /
        {{invoiceData.invoice_number}} -
        {{invoiceStatus}}
      </h3>
      <h3 class="m-0" v-if="!invoiceData.main_related_invoice && !isNewInvoice">
        {{invoiceLabel}}
      </h3>
      <h3 class="m-0" v-if="isNewInvoice">
        {{invoiceTitle}} /
      </h3>
      <div class="icons-wrapper">
        {{invoiceInfo}}
        <icon name="angle-down" class="angle" v-if="showCollapse"/>
        <icon name="angle-right" class="angle" v-else/>
      </div>
    </div>
    <b-collapse id="collapse" v-model="showCollapse">
      <div class="form-info">
        <b-col>
          <div>
            <label>{{$t('labels.invoices.invoice_number')}}</label>
            <input class="form-control" v-model="invoiceData.invoice_number"
                   placeholder=""
                   :disabled="(isApprovedInvoice || isNewInvoice) || (!currentUserCanEditInvoice)">
          </div>
          <div>
            <label class="important-field" title="This field is required to completing sales invoice">
              {{$t('labels.common.buyer')}}
            </label>
            <multiselect v-model="invoiceData.buyer" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="buyers" :multiple="false"
                         @input="loadDeliveryPartnerPlace" :class="{ required: !invoiceData.buyer}"
                         :close-on-select="true"
                         :disabled="isApprovedInvoice || !currentUserCanEditInvoice"/>
          </div>
          <div>
            <label class="important-field" title="This field is required to completing sales invoice">
              {{$t('labels.common.seller')}}
            </label>
            <multiselect v-model="invoiceData.seller" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="sellers" :multiple="false" :close-on-select="true"
                         select-label="" deselect-label="" selected-label=""
                         :disabled="isApprovedInvoice || !currentUserCanEditInvoice"
                         :class="{ required: !invoiceData.seller}"/>
          </div>
          <div>
            <label class="important-field" title="This field is required to completing sales invoice">
              {{$t('labels.invoices.invoice_date')}}
            </label>
            <datepicker v-model="invoiceData.invoice_date" :class="{ required: !invoiceData.invoice_date}"
                        lang="en" :first-day-of-week="1" :not-after="notAfterInvoiceDate"
                        placeholder=""
                        :disabled="isApprovedInvoice || !currentUserCanEditInvoice"
                        @change="fetchCurrencyRate"/>
          </div>
          <div v-if="isGeneralGroup || isCoupleGroup">
            <label>{{$t('labels.common.payment_terms')}}</label>
            <multiselect v-model="invoiceData.payment_term" :max-height="200"
                         placeholder="" label="short_name" track-by="short_name"
                         :options="payment_terms" :multiple="false" :close-on-select="true"
                         select-label="" deselect-label="" selected-label=""
                         @select="changeDateDue"
                         :disabled="isApprovedInvoice || !currentUserCanEditInvoice"/>
          </div>
          <div v-if="isGeneralGroup || isCoupleGroup">
            <label>{{$t('labels.common.due_date')}}</label>
            <datepicker v-model="invoiceData.date_due"
                        lang="en" :first-day-of-week="1"
                        placeholder=""
                        :disabled="isApprovedInvoice || !currentUserCanEditInvoice"/>
          </div>
        </b-col>
        <b-col>
          <div v-if="isGeneralGroup || isCoupleGroup">
            <label>{{$t('labels.common.incoterms')}}</label>
            <multiselect v-model="invoiceData.incoterm" :max-height="200"
                         placeholder="" label="code" track-by="code"
                         :options="incoterms" :multiple="false" :close-on-select="true"
                         select-label="" deselect-label="" selected-label=""
                         :disabled="isApprovedInvoice || !currentUserCanEditInvoice"/>
          </div>
          <div v-if="isGeneralGroup || isCoupleGroup || isDiscountGroup">
            <label>{{$t('labels.common.delivery_place')}}</label>
            <multiselect v-model="invoiceData.partner_address" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="partner_addresses" :multiple="false" :close-on-select="true"
                         select-label="" deselect-label="" selected-label=""
                         :disabled="(isBuyerNull || isApprovedInvoice) || !currentUserCanEditInvoice"/>
          </div>
          <div v-if="isGeneralGroup || isCoupleGroup || isDiscountGroup">
            <label>{{$t('labels.sale_order.receiver_of_the_load')}}</label>
            <multiselect v-model="invoiceData.receiver_of_the_load" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="delivery_addresses" :multiple="false" :close-on-select="true"
                         select-label="" deselect-label="" selected-label=""
                         :disabled="isApprovedInvoice || !currentUserCanEditInvoice"/>
          </div>
          <div v-if="isGeneralGroup || isCoupleGroup">
            <label>{{$t('labels.common.delivery_method')}}</label>
            <multiselect v-model="invoiceData.delivery_method" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="delivery_methods" :multiple="false" :close-on-select="true"
                         select-label="" deselect-label="" selected-label=""
                         :disabled="isApprovedInvoice || !currentUserCanEditInvoice"/>
          </div>
          <div v-if="isGeneralGroup || isCoupleGroup">
            <label>{{$t('labels.common.transport_nr')}}</label>
            <input class="form-control" v-model="invoiceData.transport_no"
                   placeholder=""
                   :disabled="isApprovedInvoice || !currentUserCanEditInvoice">
          </div>
          <div v-if="isGeneralGroup || isCoupleGroup">
            <label>{{$t('labels.invoice.transport_costs')}} ({{base_currency.code}})</label>
            <input class="form-control" v-model="invoiceData.total_transport"
                   placeholder=""
                   :disabled="!currentUserCanChangeTransportExpenses">
          </div>
        </b-col>
        <b-col>
          <div v-if="isCoupleGroup">
            <label>{{$t('labels.invoice.value_proc')}}</label>
            <input class="form-control" v-model="invoiceData.value"
                   placeholder=""
                   :disabled="isApprovedInvoice || !currentUserCanEditInvoice">
          </div>
          <div>
            <div>
              <label class="important-field"
                     title="This field is required to completing sales invoice">{{$t('labels.common.currency')}}</label>
              <b-badge size="sm" variant="primary" id="currRate" class="rate"
                       v-if="invoiceData.invoice_date && invoiceData.currency">i
              </b-badge>
              <b-popover target="currRate" placement="top" triggers="hover focus"
                         v-if="invoiceData.invoice_date && invoiceData.currency">
                {{$t('common_content.currency_rate_on_date',
                {course: `${base_currency.code}/${invoiceData.currency.code}`, rateDate})}} {{rate.toFixed(5)}}
              </b-popover>
            </div>
            <multiselect ref="invoiceCurrency" :value="invoiceData.currency" :max-height="200"
                         :class="{ required: !invoiceData.currency}"
                         placeholder="" label="code" track-by="code"
                         :options="currencies" :multiple="false" :close-on-select="true"
                         select-label="" deselect-label="" selected-label="" @select="getCurrencyRate"
                         :disabled="(isApprovedInvoice || isServiceType
                         || isDebitOrCreditRelatedType || isCommissionType) || !currentUserCanEditInvoice"/>
          </div>
          <div>
            <label>{{$t('tables.total')}}</label>
            <input class="form-control" :value="invoiceData.total_without_vat_str" disabled>
          </div>
          <div class="position-relative" v-if="isGeneralGroup || isCoupleGroup || isDiscountGroup">
            <label>{{$t('labels.partner.vat')}}</label>
            <multiselect v-model="invoiceData.vat" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="invoiceVats" :multiple="false" :close-on-select="true"
                         select-label="" deselect-label="" selected-label="" @input="setVatCost"
                         :disabled="isApprovedInvoice || !currentUserCanEditInvoice"/>
            <b-form-text class="position-absolute vat-sum-text">{{$t('labels.invoice.vat_sum')}}:
              {{invoiceData.vat_cost_str}}
            </b-form-text>
          </div>
          <div v-if="isGeneralGroup || isCoupleGroup || isDiscountGroup" class="vat-wrapper">
            <span>
              <b-badge size="sm" variant="primary" id="vatPopover">i</b-badge>
              <b-popover target="vatPopover" placement="top" triggers="hover focus">
                {{$t('labels.invoice.vat_toggle_info')}}
              </b-popover>
            </span>
            <toggle-button
              :width="50"
              @change="setVat"
              :value="invoiceData.paid_vat"
              :sync="true"
              class="ml-2"
              :disabled="isApprovedInvoice || !currentUserCanEditInvoice"
            />
            <span v-if="invoiceData.paid_vat" class="vat-info">
              <b>{{$t('labels.invoice.total_with_vat')}}</b>
              {{invoiceData.total_str}}
            </span>
            <span v-if="!invoiceData.paid_vat" class="vat-info">
              <b>{{$t('labels.invoice.total_without_vat')}}</b>
              {{invoiceData.total_without_vat_str}}
            </span>
          </div>
          <div>
            <label>{{$t('labels.invoice.total_sum')}}</label>
            <input class="form-control" :value="invoiceData.total_str" disabled>
          </div>
          <div>
            <label>{{$t('labels.invoice.total_sum_base_curr')}} ({{base_currency.code}})</label>
            <input class="form-control" :value="invoiceData.total_base_curr" disabled>
          </div>
        </b-col>
        <b-col>
          <div>
            <label>{{$t('labels.common.additional_sum')}} ({{base_currency.code}})</label>
            <input class="form-control" v-model="invoiceData.additional_total"
                   :disabled="isApprovedInvoice || !currentUserCanEditInvoice">
          </div>
          <div v-if="isGeneralGroup">
            <label>{{$t('tabs.common.additional_cost')}} ({{base_currency.code}})</label>
            <input class="form-control" v-model="invoiceData.additional_costs_sum"
                   :disabled="!currentUserCanEditInvoice">
          </div>
          <div v-if="isGeneralGroup">
            <label>{{$t('labels.invoice.lc_cost')}}</label>
            <input class="form-control" v-model="invoiceData.lc"
                   disabled>
          </div>
          <div>
            <label>{{$t('labels.invoice.text_notes')}}</label>
            <multiselect v-model="invoiceData.additional_information" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="text_notes" :multiple="true" :close-on-select="false"
                         select-label="" deselect-label="" selected-label=""
                         :disabled="isApprovedInvoice || !currentUserCanEditInvoice"/>
          </div>
          <div v-if="isDiscountGroup">
            <label>{{$t('labels.invoice.correction_reason')}}</label>
            <multiselect v-model="invoiceData.correction_reason" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         :options="correction_reasons" :multiple="false" :close-on-select="true"
                         select-label="" deselect-label="" selected-label=""
                         :disabled="isApprovedInvoice || !currentUserCanEditInvoice"/>
          </div>
          <div v-if="isCreditType">
            <label>{{$t('labels.common.taken_into_cost')}} ({{base_currency.code}})</label>
            <input class="form-control" v-model="invoiceData.taken_into_cost"
                   :disabled="!currentUserCanEditInvoice">
          </div>
          <div>
            <label>{{$t('labels.common.notes')}}</label>
            <b-form-textarea class="form-control" v-model="invoiceData.notes"
                             :disabled="!currentUserCanEditInvoice"/>
          </div>
        </b-col>
      </div>
    </b-collapse>
    <div class="mb-2 mt-2">
      <b-row>
        <b-col v-if="!isNewInvoice" class="d-flex" cols="10">
          <span class="text-bold text-nowrap mr-3">
            {{$t('labels.invoice.related_invoices')}}
          </span>
          <div class="d-flex flex-wrap">
            <div v-for="(invoice, idx) in invoiceData.related_invoices"
                 class="mr-2" :key="invoice.id">
              <router-link :to="`/sales-invoice/${invoice.id}`">
                {{invoice.invoice_number}}
              </router-link>
              <span class="ml-2" v-if="idx !== invoiceData.related_invoices.length - 1">/</span>
            </div>
          </div>
        </b-col>
        <b-col cols="2" class="d-flex justify-content-end" v-if="isGeneralType || isEquipmentType">
          <credit-note-compensation-modal :invoice="invoiceData"/>
          <b-popover target="invoice-creating-related" :show.sync="popoverCreateRelatedInvoice" placement="bottom"
                     ref="invoice-creating-popover">
            <div>
              <b-btn size="sm" class="full-width" title="Create credit invoice"
                     @click="createRelatedInvoice('credit')">
                {{$t('labels.invoice.credit_invoice')}}
              </b-btn>
            </div>
            <div class="mt-2">
              <b-btn size="sm" class="full-width" title="Create debit invoice" @click="createRelatedInvoice('debit')">
                {{$t('labels.invoice.debit_invoice')}}
              </b-btn>
            </div>
            <div class="mt-2">
              <b-btn size="sm" class="full-width" title="Create compensation invoice" v-if="isInvoiceHasDebitRelated"
                     @click="createCompensationInvoice">
                {{$t('labels.invoice.compensation')}}
              </b-btn>
            </div>
            <div class="mt-2" v-if="!isInvoiceHasCorrectionInvoice">
              <b-btn size="sm" class="full-width" title="Create margin correct invoice"
                     @click="createRelatedInvoice('margin_correct')">
                {{$t('labels.invoice.margin_correct')}}
              </b-btn>
            </div>
          </b-popover>
          <b-btn variant="border-0" id="invoice-creating-related" size="sm"
                 class="d-flex align-items-center font-weight-normal"
                 :disabled="!currentUserCanEditInvoice">
            <icon name="plus-circle" class="text-secondary mr-3"/>
            {{$t('buttons.sales_invoices.create_related_invoice')}}
          </b-btn>
        </b-col>
      </b-row>
    </div>
    <b-card no-body v-if="!isNewInvoice">
      <add-packages-modal
        v-if="tabsRestrictions['shipping-specification'] && (isGeneralType || isCommissionType)"
        :add-packages-to-invoice-cb="addPackagesToInvoice"/>
      <edit-invoice-item-modal
        v-if="tabsRestrictions['shipping-specification'] && (isCreditType || isDebitType || isPartnerType ||
          isEquipmentType || isServiceType || isCompensationType || isMarginCorrectType)"
        :related-invoice-id="relatedInvoiceId" :invoice-id="invoiceId"
        :invoice-currency="invoiceData.currency"
        :is-invoice-related="isInvoiceRelated" :fetch-invoice-items-cb="fetchInvoiceItemsCallback"
        :invoice-type-name="invoiceTypeName" :invoice-link="commonUrl"/>
      <edit-invoice-item-modal
        v-if="tabsRestrictions['shipping-specification'] && (isGeneralType || isCommissionType)"
        :related-invoice-id="relatedInvoiceId" :invoice-id="invoiceId"
        :invoice-currency="invoiceData.currency"
        :is-invoice-related="isInvoiceRelated" :fetch-packages-cb="fetchInvoicePackagesCallback"
        :invoice-type-name="invoiceTypeName" :invoice-link="commonUrl"/>
      <b-tabs card v-if="showTabs" v-model="currentTabIndex"
            ref="invoiceTabs"
        >
        <b-tab v-if="tabsRestrictions['margins']" tab-name='margins'
               @click="setGridData(marginsGrid, margins, 'margins')"
               :active="currentTabName === 'margins'">
          <template slot="title">
            <portal-target name="margins_tab"/>
          </template>
          <portal to="margins_tab">{{$t('tabs.sales_invoice.main')}}</portal>
          <div class="d-flex justify-content-end button-group">
            <b-btn variant="outline-secondary" class="d-flex align-items-center" @click="generateMR" size="sm">
              <icon name="file-excel" class="mr-3"/>
              {{$t('buttons.sales_invoices.margin_report')}}
            </b-btn>
          </div>
          <ag-grid-vue :gridOptions="marginsGrid" :style="gridHeight" class="ag-theme-balham grid"
                       :columnDefs="marginsGridColumns" :postProcessPopup="postProcessPopup"
                       :rowData="margins"
          />
          <b-row class="text-bold">
            <b-col>
              <div>{{$t('labels.invoice.margin_turnover')}} {{marginTurnover}}</div>
              <div>{{$t('labels.invoice.margin_expense')}} {{marginExpense}}</div>
            </b-col>
            <b-col class="text-right">
              <div>{{$t('labels.invoice.margin')}} {{marginBaseCurr}}</div>
              <div>{{marginProc}}</div>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab v-if="tabsRestrictions['orders-and-shipment']"
               @click="setGridData(ordersGrid, orders, 'orders')"
               tab-name="orders"
               :active="currentTabName === 'orders'">
          <template slot="title">
            <portal-target name="orders_tab"/>
          </template>
          <portal to="orders_tab">{{$t('tabs.sales_invoice.orders_shipment')}}</portal>
          <ag-grid-vue :gridOptions="ordersGrid" :style="gridHeight" class="ag-theme-balham grid"
                       :columnDefs="ordersGridColumns" :postProcessPopup="postProcessPopup"
                       :rowData="orders"
          />
        </b-tab>
        <b-tab v-if="tabsRestrictions['shipping-specification'] && (isGeneralType || isCommissionType)"
               @click="setGridData(invoicePackageGrid, invoicePackages, 'invoice-packages')"
               tab-name="invoice-packages"
               :active="currentTabName === 'invoice-packages'">
          <template slot="title">
            <portal-target name="shipping_specification_tab"/>
          </template>
          <portal to="shipping_specification_tab">{{$t('tabs.sales_invoice.shipping_specification')}}</portal>
          <div class="d-flex justify-content-between">
            <div>
              <span class="mr-2">{{$t('labels.invoice.selected_invoice_items')}}: {{invoiceItemsPackagesCount}}</span>
              <span>{{$t('labels.invoice.selected_invoice_packages')}}: {{invoicePackagesCount}}</span>
            </div>
            <div class="d-flex button-group" v-if="!isCommissionType && !isApprovedInvoice">
              <b-btn variant="success" @click="addPackages" size="sm"
                     v-if="isGeneralGroup || isDiscountGroup || isAdditionalGroup"
                     :disabled="!currentUserCanEditInvoice">
                {{$t('buttons.common.add')}}
              </b-btn>
              <b-btn variant="danger" @click="deletePackages" size="sm"
                     :disabled="!currentUserCanEditInvoice">
                {{$t('buttons.common.delete')}}
              </b-btn>
            </div>
          </div>
          <ag-grid-vue :gridOptions="invoicePackageGrid" :style="gridHeight" class="ag-theme-balham grid"
                       :columnDefs="invoicePackageGridColumns" @rowSelected="countSelectedInvoicePackages"
                       :postProcessPopup="postProcessPopup" :rowData="invoicePackages"
                       @rowDoubleClicked="editInvoiceItem"
          />
        </b-tab>
        <b-tab
          v-if="tabsRestrictions['shipping-specification'] && (isCreditType || isDebitType || isPartnerType ||
            isEquipmentType || isServiceType || isCompensationType || isMarginCorrectType)"
          @click="setGridData(invoiceItemGrid, invoiceItems, 'invoice-items')"
          tab-name="invoice-items"
          :active="currentTabName === 'invoice-items'">
          <template slot="title">
            <portal-target name="shipping_specification_tab"/>
          </template>
          <portal to="shipping_specification_tab">{{$t('tabs.sales_invoice.shipping_specification')}}</portal>
          <div class="d-flex justify-content-between">
            <div>
              {{$t('labels.invoice.selected_invoice_items')}}: {{invoiceItemsCount}}
            </div>
            <div class="d-flex button-group" v-if="!isDiscountType && !isServiceType && !isApprovedInvoice">
              <b-btn variant="success" @click="addInvoiceItems" size="sm"
                     v-if="isGeneralGroup || isDiscountGroup || isAdditionalGroup"
                     :disabled="!currentUserCanEditInvoice">
                {{$t('buttons.common.add')}}
              </b-btn>
              <b-btn variant="danger" @click="deleteInvoiceItems" size="sm"
                     :disabled="!currentUserCanEditInvoice">
                {{$t('buttons.common.delete')}}
              </b-btn>
            </div>
          </div>
          <ag-grid-vue :gridOptions="invoiceItemGrid" :style="gridHeight" class="ag-theme-balham grid"
                       :columnDefs="invoiceItemGridColumns" @rowSelected="countSelectedInvoiceItems"
                       :postProcessPopup="postProcessPopup" :rowData="invoiceItems" @rowDoubleClicked="editInvoiceItem"
          />
        </b-tab>
        <b-tab v-if="tabsRestrictions['commission-fees']"
               :active="currentTabName === 'commission-fees'"
               tab-name="commission-fees"
               @click="setGridData(commissionFeesGrid, commissionFees, 'commission-fees')">
          <sales-invoice-commission-fee-modal :parent-invoice-id="invoiceId"
                                              :fetch-commission-fees-cb="fetchCommissionFeesCallback"
                                              :customer-id="customerId"/>
          <template slot="title">
            <portal-target name="commission_fees_tab"/>
          </template>
          <portal to="commission_fees_tab">{{$t('tabs.sales_invoice.commission_fees')}}</portal>
          <div class="d-flex justify-content-between">
            <div>
              {{$t('labels.common.selected')}}: {{selectedRowsCount}}
            </div>
            <div class="d-flex button-group" v-if="currentUserCanEditInvoiceAgentFees">
              <b-btn variant="success" @click="openAddCommissionFeeModal" size="sm">
                {{$t('buttons.common.add')}}
              </b-btn>
              <b-btn variant="danger" @click="deleteCommissionFee" size="sm">
                {{$t('buttons.common.delete')}}
              </b-btn>
            </div>
          </div>
          <ag-grid-vue :gridOptions="commissionFeesGrid" :style="gridHeight" class="ag-theme-balham grid"
                       :columnDefs="commissionFeesGridColumns" @rowDoubleClicked="openEditCommissionFeeModal"
                       :postProcessPopup="postProcessPopup" :rowData="commissionFees" @rowSelected="countSelectedRows"
                       @rowDataChanged="countSelectedRows"
          />
        </b-tab>
        <b-tab v-if="tabsRestrictions['payments']"
               :active="currentTabName === 'payments'"
               tab-name="payments"
               @click="setGridData(paymentsGrid, payments, 'payments')">
          <template slot="title">
            <portal-target name="payments_tab"/>
          </template>
          <portal to="payments_tab">
            {{$t('tabs.sales_invoice.payments')}}
          </portal>
          <payments-charge-off-modal :payments-charge-off-to-invoice-cb="paymentsChargeOffToInvoice"/>
          <div class="d-flex justify-content-end button-group">
            <b-btn variant="primary" @click="openChargeOffModal" :disabled="isInvoiceChargedOff" size="sm">
              {{$t('buttons.payments.charge_off_money')}}
            </b-btn>
            <b-btn variant="secondary" @click="cancelChargeOff" :disabled="!isInvoiceChargedOff" size="sm">
              {{$t('buttons.payments.cancel_charge_off')}}
            </b-btn>
            <b-btn variant="outline-secondary" class="d-flex align-items-center" size="sm" @click="excelExport"
                   title="Excel export">
              <icon name="file-excel" class="mr-2"/>
              {{$t('buttons.common.export')}}
            </b-btn>
          </div>
          <ag-grid-vue :gridOptions="paymentsGrid" :style="gridHeight" class="ag-theme-balham grid"
                       :postProcessPopup="postProcessPopup" :columnDefs="paymentsGridColumns" :rowData="payments"
          />
        </b-tab>
        <b-tab v-if="tabsRestrictions['documents']" @click="fetchDocuments">
          <template slot="title">
            <portal-target name="documents_tab"/>
          </template>
          <portal to="documents_tab">
            {{$t('tabs.common.documents')}}
          </portal>
          <app-documents :isCollapse="showCollapse" :documents="documents"
                         :url="`accounting/invoices/${invoiceId}/attachments/`"
                         @loadDocuments="fetchDocuments"/>
        </b-tab>
        <b-tab v-if="tabsRestrictions['comments']" @click="fetchComments">
          <template slot="title">
            <portal-target name="comments_tab"/>
          </template>
          <portal to="comments_tab">{{$t('tabs.common.comments')}}</portal>
          <app-comments :comments="comments" :url="`${commonUrl}comments/`"
                        @loadComments="fetchComments"/>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import moment from 'moment';
  import { mapGetters, mapState } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import Datepicker from 'vue2-datepicker';

  import gridProps from '@/gridProps';

  import Documents from '@/components/common/Documents';
  import Comments from '@/components/common/Comments';
  import HistoryModal from '@/components/modals/HistoryModal';
  import DocumentsTemplatesModal from '@/components/modals/DocumentsTemplatesModal';
  import PaymentsChargeOffModal from '@/components/modals/PaymentsChargeOffModal';
  import EditInvoiceModal from '@/components/modals/EditInvoiceItemModal';
  import EditInvoiceItemModal from '@/components/modals/EditInvoiceItemModal';
  import CreditNoteCompensationModal from '@/components/modals/CreditNoteCompensationModal';

  import {
    AccumSumRows,
    addColIdToColumns,
    getArrayByProperty,
    getFileNameFromContentDisposition, getFilteredNodes,
    handlerResponse,
    isNull,
    setNotifyData,
    SumTextRows,
    cleanFilters
  } from '@/helpers/commonHelpers';
  import {
    addGroupingInResponseData,
    alignCells,
    alignForNumber,
    autoResize,
    formatNumber,
    getNodeChildDetails,
    LinkCellRenderer,
    LinkCellRendererSalesAgents,
    notificationsRenderer
  } from '@/helpers/renderHelpers';
  import { DATE_FORMAT } from '@/constants';
  import {
    COMMISSION,
    COMPENSATION,
    CREDIT,
    DEBIT,
    EQUIPMENT,
    INVOICE,
    MARGIN_CORRECT,
    PARTNER,
    SERVICE
  } from '@/constants/invoiceTypes';
  import SalesInvoiceCommissionFeeModal from '@/components/modals/SalesInvoiceCommissionFeeModal';
  import { translateColumns } from '@/helpers/translate-helper';
  import AddPackagesModal from '@/components/modals/AddPackagesModal';
  import { gridMixins } from '@/mixins';
  import { SumRows } from '@/helpers/commonHelpers'

  export default {
    mixins: [gridMixins],
    components: {
      EditInvoiceItemModal,
      AddPackagesModal,
      PaymentsChargeOffModal,
      SalesInvoiceCommissionFeeModal,
      EditInvoiceModal,
      HistoryModal,
      DocumentsTemplatesModal,
      CreditNoteCompensationModal,
      Multiselect,
      'ag-grid-vue': AgGridVue,
      'app-documents': Documents,
      'app-comments': Comments,
      Datepicker
    },
    data() {
      return {
        invoiceData: {
          additional_costs_sum: 0,
          additional_information: [],
          additional_total: 0,
          buyer: null,
          correction_reason: null,
          currency: null,
          date_due: null,
          delivery_method: null,
          incoterm: null,
          invoice_date: '',
          lc: 0,
          notes: '',
          paid_vat: true,
          partner_address: null,
          payment_term: null,
          receiver_of_the_load: null,
          seller: null,
          total: 0,
          total_agent_fee: 0,
          total_transport: 0,
          transport_no: '',
          value: 0,
          taken_into_cost: 0,
          vat: null,
          total_with_vat_str: '',
          total_without_vat_str: '',
          total_without_vat: 0,
          total_str: '',
          total_base_curr_str: ''
        },
        partner_addresses: [],
        showCollapse: true,
        popoverPrintWithStatus: false,
        rate: 0,
        rateDate: '',
        popoverCreateRelatedInvoice: false,
        gridOptions: {
          rowSelection: 'multiple',
          rowGroupPanelShow: 'always',
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            enableValue: true,
            enableRowGroup: true,
            valueFormatter(params) {
              return formatNumber(params.value);
            },
            cellStyle(params) {
              return alignCells(params);
            },
          },
          onRowDataChanged() {
            if (this.rowData && this.rowData.length) {
              autoResize(this);
            } else {
              this.api && this.api.hideOverlay();
              this.api && this.api.showNoRowsOverlay();
            }
          },
          onGridReady() {
            if (!this.rowData.length) {
              this.api && this.api.showNoRowsOverlay();
            }
          },
          ...gridProps
        },
        comments: [],
        documents: [],
        marginsGrid: {},
        marginsGridColumns: [],
        margins: [],
        ordersGrid: {},
        ordersGridColumns: [],
        invoiceItemGrid: {},
        invoiceItemGridColumns: [],
        invoiceItems: [],
        invoicePackageGrid: {},
        invoicePackageGridColumns: [],
        invoicePackages: [],
        commissionFeesGrid: {},
        commissionFeesGridColumns: [],
        commissionFees: [],
        orders: [],
        paymentsGrid: {},
        paymentsGridColumns: [
          {
            'field': 'document_nr',
            colId: 'document_nr',
            'headerName': 'document',
            'cellRenderer': 'PaymentLink',
            'minWidth': 100
          },
          {'field': 'document_date', colId: 'document_date', 'headerName': 'document_date'},
          {'field': 'payment_date', colId: 'payment_date', 'headerName': 'payment_date'},
          {'field': 'payment_sum_str', colId: 'payment_sum_str', 'headerName': 'payment_sum'},
          {
            'headerName': 'document_sum', 'children': [
              {
                'field': 'document_sum_in_invoice_currency_str',
                colId: 'document_sum_in_invoice_currency_str',
                'headerName': 'account_abbr'
              },
              {
                'field': 'document_additional_sum_str',
                colId: 'document_additional_sum_str',
                'headerName': 'additional_abbr'
              }
            ]
          },
          {
            'headerName': 'rate', 'children': [
              {'field': 'currencies_str', colId: 'currencies_str', 'headerName': '', 'minWidth': 85},
              {'field': 'currency_rate', colId: 'currency_rate', 'headerName': '', 'minWidth': 85}
            ]
          },
          {
            'headerName': 'document_sum_base_curr', 'children': [
              {
                'field': 'document_sum_in_base_curr_str',
                colId: 'document_sum_in_base_curr_str',
                'headerName': 'account_abbr',
                'width': 80
              },
              {
                'field': 'additional_sum_base_curr',
                colId: 'additional_sum_base_curr',
                'headerName': 'additional_abbr',
                'width': 80
              }
            ]
          },
          {
            'field': 'rate_difference_in_base_curr_str',
            colId: 'rate_difference_in_base_curr_str',
            'headerName': 'currency_rate_diff',
            'width': 125
          }
        ],
        payments: [],
        selectedRowsCount: 0,
        invoiceItemsPackagesCount: 0,
        invoicePackagesCount: 0,
        invoiceItemsCount: 0,
        showTabs: false,
        marginBaseCurr: '',
        marginProc: '',
        marginTurnover: '',
        marginExpense: '',
        currentTabName: 'margins',
        currentTabIndex: 0,
        notAfterInvoiceDate: null,
        popoverDocumentTemplateType: false,
        documentsTemplateNameUploadUrl: '',
        documentsTemplateType: ''
      }
    },
    computed: {
      ...mapGetters('woodware', ['buyers', 'sellers']),
      ...mapState('woodware', ['payment_terms', 'incoterms', 'delivery_addresses', 'delivery_methods', 'text_notes',
        'correction_reasons', 'base_currency']),
      ...mapState('generic', ['currencies', 'invoiceVats', 'currentRoutePermissions']),
      customerId() {
        return this.invoiceData.buyer && this.invoiceData.buyer.id;
      },
      invoiceId() {
        return this.$route.params.id;
      },
      invoiceType() {
        return this.$route.params.type;
      },
      invoiceTypeName() {
        return this.invoiceData.type;
      },
      billTitle() {
        return `${this.$t('buttons.common.bill')} ${(this.$t('labels.invoice.invoice')).toLowerCase()}`;
      },
      isNewInvoice() {
        return this.invoiceId === 'new';
      },
      isApprovedInvoice() {
        return this.invoiceData.status && this.invoiceData.status.key === 'approved';
      },
      isGeneralType() {
        return this.invoiceTypeName === INVOICE;
      },
      isPartnerType() {
        return this.invoiceTypeName === PARTNER;
      },
      isEquipmentType() {
        return this.invoiceTypeName === EQUIPMENT;
      },
      isCreditType() {
        return this.invoiceTypeName === CREDIT;
      },
      isDiscountType() {
        return this.isCreditType && this.invoiceData.auto_created_invoice;
      },
      isDebitType() {
        return this.invoiceTypeName === DEBIT;
      },
      isServiceType() {
        return this.invoiceTypeName === SERVICE;
      },
      isCommissionType() {
        return this.invoiceTypeName === COMMISSION;
      },
      isCompensationType() {
        return this.invoiceTypeName === COMPENSATION;
      },
      isMarginCorrectType() {
        return this.invoiceTypeName === MARGIN_CORRECT;
      },
      isDebitRelatedType() {
        return this.isDebitType && this.isInvoiceRelated;
      },
      isCreditRelatedType() {
        return this.isCreditType && this.isInvoiceRelated;
      },
      isDebitOrCreditRelatedType() {
        return this.isDebitRelatedType || this.isCreditRelatedType;
      },
      isGeneralGroup() {
        return this.invoiceTypeName === INVOICE ||
          (this.invoiceTypeName === PARTNER || this.invoiceType === 'partner_invoice') ||
          (this.invoiceTypeName === EQUIPMENT || this.invoiceType === 'equipment_invoice');
      },
      isDiscountGroup() {
        return (this.invoiceTypeName === CREDIT || this.invoiceType === 'credit_invoice') ||
          (this.invoiceTypeName === DEBIT || this.invoiceType === 'debit_invoice');
      },
      isCoupleGroup() {
        return this.invoiceTypeName === SERVICE || this.invoiceTypeName === COMMISSION;
      },
      isAdditionalGroup() {
        return this.invoiceTypeName === COMPENSATION || this.invoiceTypeName === MARGIN_CORRECT;
      },
      invoiceStatus() {
        return this.invoiceData.status && this.invoiceData.status.name || '';
      },
      invoiceInfo() {
        return this.isNewInvoice ? '' : `${this.invoiceData.created || ''} / ${this.$i18n.t('common_content.creator')}:
         ${this.invoiceData.created_by || ''}`;
      },
      invoiceTitle() {
        if (this.isNewInvoice) {
          return this.$t(`labels.invoice.${this.invoiceType}`);
        } else {
          const type = this.invoiceTypeName ? this.invoiceTypeName : INVOICE;
          const invoiceTypeRelations = {
            [INVOICE]: 'invoice',
            [SERVICE]: 'service_invoice',
            [COMMISSION]: 'commission_invoice',
            [PARTNER]: 'partner_invoice',
            [EQUIPMENT]: 'equipment_invoice',
            [CREDIT]: 'credit_invoice',
            [DEBIT]: 'debit_invoice',
            [COMPENSATION]: 'compensation',
            [MARGIN_CORRECT]: 'mc_invoice'
          };

          return this.$t(`labels.invoice.${invoiceTypeRelations[type]}`);
        }
      },
      invoiceLabel() {
        return `${this.invoiceTitle} / ${this.invoiceData && this.invoiceData.invoice_number ?
          this.invoiceData.invoice_number : ''} - ${this.invoiceStatus}`;
      },
      historySettings() {
        return {url: `invoice/?id=${this.invoiceId}`, title: this.invoiceLabel.split('/')[1]}
      },
      isBuyerNull() {
        return isNull(this.invoiceData.buyer);
      },
      gridHeight() {
        if (window.innerHeight > 1024) {
          return this.showCollapse ? {height: 'calc(100vh - 565px)'} : {height: 'calc(100vh - 365px)'};
        } else {
          return this.showCollapse ? {height: '260px'} : {height: 'calc(100vh - 365px)'};
        }
      },
      commonUrl() {
        return `accounting/invoices/${this.invoiceId}/`;
      },
      relatedInvoiceId() {
        return this.invoiceData.initial_invoice && this.invoiceData.initial_invoice.id;
      },
      isInvoiceRelated() {
        return !!this.invoiceData.initial_invoice;
      },
      isInvoiceHasDebitRelated() {
        return !!this.invoiceData.related_invoices.filter(invoice => invoice.type === CREDIT &&
          invoice.auto_created_invoice === false).length;
      },
      isInvoiceHasCorrectionInvoice() {
        return !!this.invoiceData.related_invoices.filter(invoice => invoice.type === MARGIN_CORRECT).length;
      },
      isInvoiceChargedOff() {
        return this.invoiceData.balance === 0;
      },
      tabsRestrictions() {
        return {
          'margins': !this.isNewInvoice && this.currentRoutePermissions.view_margin,
          'orders-and-shipment': !this.isNewInvoice && this.isGeneralType,
          'shipping-specification': !this.isNewInvoice,
          'commission-fees': !this.isNewInvoice && this.isApprovedInvoice && (this.isGeneralType || this.isServiceType
            || this.isEquipmentType || this.isCreditType || this.isDebitType),
          'payments': !this.isNewInvoice && this.isApprovedInvoice && this.currentRoutePermissions.view_invoice_payments,
          'documents': !this.isNewInvoice,
          'comments': !this.isNewInvoice,
        }
      },
      currentUserCanEditInvoice() {
        return this.currentRoutePermissions.change_invoice;
      },
      currentUserCanChangeTransportExpenses() {
        return this.currentRoutePermissions.change_transport_expenses;
      },
      currentUserCanEditInvoiceAgentFees() {
        return this.currentRoutePermissions.edit_invoice_agent_fees;
      },
      isInvoiceDateLaterThenToday() {
          return moment(this.invoiceData.invoice_date).format(DATE_FORMAT) > moment().format(DATE_FORMAT);
      }
    },
    methods: {
      getWidth (items, key) {
        const PIXEL_TO_CHAR_RATIO = 7
        const width = Math.max(...items.map(item => item[key].length)) * PIXEL_TO_CHAR_RATIO
        this.invoicePackageGrid.autoGroupColumnDef.width = width
        this.invoicePackageGrid.autoGroupColumnDef.minWidth = width
      },
      async fetchInvoiceData(rerenderTabs) {
        if (rerenderTabs) {
          this.showTabs = false;
          this.currentTabIndex = 0;
          this.currentTabName = null;
        }
        await this.$http.get(`${this.commonUrl}`)
          .then(handlerResponse)
          .then(async response => {
            this.invoiceData = {...this.invoiceData, ...response};
            await this.$store.dispatch('location/setPathName', response.invoice_number);
            this.setVatCost();
            await this.loadDeliveryPartnerPlace();
            rerenderTabs && (this.showTabs = true);
            await this.fetchCurrencyRate();
          });
        rerenderTabs && (await this.fetchTabDataByName());
      },
      async fetchTabDataByName(){
        const tabs = this.$refs['invoiceTabs'] && this.$refs['invoiceTabs'].tabs;
        const activeTab = tabs.find(tab => tab.localActive === true);
        const tabName = activeTab.$el.getAttribute('tab-name');
        await this.fetchTabByName(tabName);
      },
      openHistoryModal() {
        this.$root.$emit('openHistoryModal', this.historySettings);
      },
      openTemplatesModal(templateType) {
        this.popoverDocumentTemplateType = false;
        const templateNameUploadUrlRelations = {
          'invoice': `accounting/invoices/${this.invoiceId}/generate_xls_from_template/`,
          'packing_list': `accounting/invoices/${this.invoiceId}/generate_packing_list_xls_from_template/`
        };
        this.documentsTemplateNameUploadUrl = templateNameUploadUrlRelations[templateType];
        this.documentsTemplateType = templateType;
        this.$nextTick(() => {
          this.$root.$emit('openDocumentsTemplatesModal');
        })
      },
      printInvoice(printType) {
        this.$http.post(`${this.commonUrl}generate_document/`, {
          grouping_type: printType
        }).then(async ({body}) => {
          await this.fetchDocuments();
          const newTab = window.open();
          newTab.document.write(body);
          this.popoverPrintWithStatus = false;
        }).catch(error => notificationsRenderer(error.data, 'error'));
      },
      async loadDeliveryPartnerPlace() {
        try {
          const params = {
            partner_id: this.invoiceData.buyer && this.invoiceData.buyer.id
          };
          const payload = await this.$http.get('woodware/list/delivery_addresses/', {params}).then(handlerResponse);
          this.partner_addresses = payload;
        } catch (error) {
          notificationsRenderer(error.data, 'error');
          throw error.data;
        }
      },
      setVat() {
        this.invoiceData.paid_vat = !this.invoiceData.paid_vat;
      },
      async fetchDocuments() {
        try {
          this.documents = await this.$http.get(`${this.commonUrl}attachments/`).then(handlerResponse);
        } catch (error) {
          notificationsRenderer(error.data, 'error');
        }
      },
      async fetchComments() {
        try {
          this.comments = await this.$http.get(`${this.commonUrl}comments/`).then(handlerResponse);
        } catch (e) {
          notificationsRenderer(e.data, 'error');
        }
      },
      changeDateDue(paymentTerm) {
        const value = paymentTerm && paymentTerm.value;
        if (this.invoiceData.payment_term) {
          this.invoiceData.date_due = this.invoiceData.date_due ? moment(this.invoiceData.date_due).add(value, 'days') : null;
        }
      },
      async getCurrencyRate(currency) {
        const baseCurr = this.base_currency && this.base_currency.code;
        if ((baseCurr.toLowerCase() !== currency.code.toLowerCase()) && this.isInvoiceDateLaterThenToday) {
          this.$swal.fire({
            title: 'Attention',
            text: this.$i18n.t(`alerts.select_not_base_currency`, {baseCurr}),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#1bc14d',
            cancelButtonColor: '#d33',
            cancelButtonText: this.$i18n.t(`common_content.no`),
            confirmButtonText: this.$i18n.t(`common_content.yes`)
          }).then(async result => {
            if (result.value) {
              this.invoiceData.currency = currency;
              this.invoiceData.invoice_date = moment(new Date()).format(DATE_FORMAT);
              this.notAfterInvoiceDate = moment(new Date()).format(DATE_FORMAT);
              await this.fetchCurrencyRate();
              if (!this.isNewInvoice) {
                this.$notify(setNotifyData('', this.$t('notifications.sales_invoice.invoice_sum_will_update'), 'warn'));
              }
            }
          })
        } else {
          this.invoiceData.currency = currency;
          this.notAfterInvoiceDate = null;
          await this.fetchCurrencyRate();
          if (!this.isNewInvoice) {
            this.$notify(setNotifyData('', this.$t('notifications.sales_invoice.invoice_sum_will_update'), 'warn'));
          }
        }
      },
      async fetchCurrencyRate() {
        if (this.invoiceData.invoice_date && this.invoiceData.currency) {
          try {
            const params = {
              date: moment(this.invoiceData.invoice_date).format(DATE_FORMAT),
              dest_curr: this.invoiceData.currency.code
            };
            const response = await this.$http.get(`woodware/list/base_currency_rate/`, {params}).then(handlerResponse);
            this.rate = response.rate;
            this.rateDate = response.date;
          } catch (e) {
            notificationsRenderer(e.data, 'error');
          }
        } else {
          this.rate = 0;
        }
      },
      setVatCost() {
        const vat = this.invoiceData.vat && +this.invoiceData.vat.value || 0;
        const vatCost = ((+vat / 100) * +this.invoiceData.total_without_vat).toFixed(2);
        const preSymbol = this.invoiceData.currency.pre_symbol ? `${this.invoiceData.currency.pre_symbol} ` : '';
        const postSymbol = this.invoiceData.currency.post_symbol ? ` ${this.invoiceData.currency.post_symbol}` : '';
        this.invoiceData.vat_cost_str = `${preSymbol}${vatCost}${postSymbol}`;
      },
      async fetchTabByName(tabName) {
        this.currentTabName = tabName;
        const tabFetchMap = {
          'margins': this.fetchMargins,
          'orders': this.fetchOrders,
          'invoice-items': this.fetchInvoiceItems,
          'invoice-packages': this.fetchInvoicePackages,
          'commission-fees': this.fetchCommissionFees,
          'payments': this.fetchPayments
        };
        await tabFetchMap[tabName]();
      },
      async setGridData(grid, gridData, tabName) {
        grid.api.setRowData(gridData);
        await this.fetchTabByName(tabName);
        autoResize(grid);
      },
      async fetchMargins() {
        try {
          const {data: {margin_table, margin, margin_perc, turnover, expenses}, used_columns } =
            await this.$http.get(`${this.commonUrl}finance_data/`).then(handlerResponse);
          this.margins = addGroupingInResponseData(margin_table);
          this.marginBaseCurr = margin;
          this.marginProc = margin_perc;
          this.marginTurnover = turnover;
          this.marginExpense = expenses;
          this.marginsGridColumns = addColIdToColumns(used_columns);
        } catch (e) {
          notificationsRenderer(e.data, 'error');
        }
      },
      async fetchOrders() {
        try {
          const response = await this.$http.get(`${this.commonUrl}orders/`).then(handlerResponse);
          this.orders = addGroupingInResponseData(response.data);
          this.ordersGridColumns = addColIdToColumns(response.used_columns);
        } catch (e) {
          notificationsRenderer(e.data, 'error');
        }
      },
      async fetchInvoiceItems() {
        try {
          const response = await this.$http.get(`${this.commonUrl}invoice_items/`).then(handlerResponse);
          this.invoiceItems = response.data;
          this.invoiceItemGridColumns = addColIdToColumns(response.used_columns);
          this.invoiceItemsCount = 0;
        } catch (e) {
          notificationsRenderer(e.data, 'error');
        }
      },
      async fetchInvoicePackages() {
        try {
          const response = await this.$http.get(`${this.commonUrl}packages/`).then(handlerResponse);
          this.invoicePackages = response.data;
          this.invoicePackageGridColumns = addColIdToColumns(response.used_columns);
          this.invoiceItemsPackagesCount = 0;
          this.invoicePackagesCount = 0;
          this.getWidth(this.invoicePackages, 'invoice_item_unique_name')
          console.log(this.invoicePackageGridColumns)
        } catch (e) {
          notificationsRenderer(e.data, 'error');
        }
      },
      async fetchCommissionFees() {
        try {
          const params = {
            invoice: this.invoiceId
          };
          const response = await this.$http.get(`accounting/sale_agent_commission_fees/`, {params}).then(handlerResponse);
          this.commissionFees = addGroupingInResponseData(response.data.results);
          this.commissionFeesGridColumns = addColIdToColumns(response.used_columns);
        } catch (e) {
          notificationsRenderer(e.data, 'error');
        }
      },
      async fetchPayments() {
        await this.fetchInvoiceData()
        try {
          const response = await this.$http.get(`${this.commonUrl}payments/`).then(handlerResponse);
          this.payments = response.map(item => {
            if (item.row_type === 'difference') {
              item.additional_sum_base_curr = item.difference_additional_sum_str;
            } else {
              item.additional_sum_base_curr = item.document_additional_sum_str;
            }
            return item;
          });
        } catch (e) {
          notificationsRenderer(e.data, 'error');
        }
      },
      gridsInitialize() {
        this.marginsGrid = {
          ..._.cloneDeep(this.gridOptions),
          // treeData: true,
          getNodeChildDetails: getNodeChildDetails,
          rowGroupPanelShow: 'none',
          components: {
            'InvoiceLink': LinkCellRendererSalesAgents('invoice_id', '/sales-invoice/'),
            'BuyerLink': LinkCellRenderer('buyer_id', '/partners/')
          },
          defaultColDef: {
            ...this.gridOptions.defaultColDef,
            valueFormatter(params) {
              return formatNumber(params.value);
            },
            cellStyle(params) {
              return alignCells(params);
            },
          },
        };
        this.ordersGrid = {
          ..._.cloneDeep(this.gridOptions),
          // TODO: wtf with this function declaration? i dont get it.
          getNodeChildDetails: (rowItem) => {
            if (rowItem.children && rowItem.children.length) {
              return {
                expanded: true,
                group: true,
                children: rowItem.children,
                key: rowItem.grouping
              };
            }

            return null;
          },
          rowGroupPanelShow: 'none',
          components: {
            'SalesOrderLink': LinkCellRenderer('id', '/sales-orders/'),
            'goToOperation': LinkCellRenderer('id', '/operation/')
          },
          defaultColDef: {
            ...this.gridOptions.defaultColDef,
            valueFormatter(params) {
              if (params.colDef.field === 'current_m3') {
                return formatNumber(params.value, 3);
              } else if (params.colDef.field === 'current_pieces') {
                return formatNumber(params.value, 0);
              } else {
                return formatNumber(params.value);
              }
            },
            cellStyle(params) {
              return alignCells(params);
            }
          },
        };
        this.invoicePackageGrid = {
          ..._.cloneDeep(this.gridOptions),
          rowGroupPanelShow: 'none',
          groupSelectsChildren: true,
          suppressAggFuncInHeader: true,
          // groupDefaultExpanded: -1,
          components: {
            'SalesOrderLink': LinkCellRenderer('so_id', '/sales-orders/'),
            'BuyerLink': LinkCellRenderer('buyer_id', '/partners/')
          },
          defaultColDef: {
            ...this.gridOptions.defaultColDef,
            valueFormatter(params) {
              return formatNumber(params.value);
            },
            cellStyle(params) {
               if (params.data && params.data.price) {
                  return {...alignCells(params), textAlign: 'right'};
               } else {
                  return alignCells(params)
               }
            }
          },
          autoGroupColumnDef: {
            headerName: this.$t('labels.invoice.invoice_item'),
            checkboxSelection: true,
            // minWidth: 550,
            // width: 550
          },
          aggFuncs: {
            'SummText': SumTextRows,
            'AccumSum': AccumSumRows,
            'SumRows': SumRows
          },
          getRowStyle(params) {
            if (!params.data) {
              return {'font-weight': 'bold'}
            }
          }
        };
        this.invoiceItemGrid = {
          ..._.cloneDeep(this.gridOptions),
          rowGroupPanelShow: 'none',
          components: {
            'SalesOrderLink': LinkCellRenderer('so_id', '/sales-orders/')
          },
          defaultColDef: {
            ...this.gridOptions.defaultColDef,
            valueFormatter(params) {
              if (params.colDef.field === 'invoice_correction_percent') {
                return formatNumber(params.value, 3);
              } else {
                return formatNumber(params.value);
              }
            },
            cellStyle(params) {
              return alignCells(params);
            }
          },
          aggFuncs: {
            'SummText': SumTextRows
          }
        };
        this.commissionFeesGrid = {
          ..._.cloneDeep(this.gridOptions),
          getNodeChildDetails: getNodeChildDetails,
          // treeData: true,
          rowGroupPanelShow: 'none',
          components: {
            'AgentLink': LinkCellRenderer('agent_id', '/partners/'),
            'AgentInvoiceLink': LinkCellRenderer('sale_agent_invoice_id', '/sale-agent-invoices/'),
            'SellerLink': LinkCellRenderer('seller', '/partners/'),
            'InvoiceLink': LinkCellRenderer('invoice_id', '/sales-invoice/'),
            'BuyerLink': LinkCellRenderer('buyer_id', '/partners/')
          },
          aggFuncs: {
            'SumRows': SumRows
          },
          defaultColDef: {
            ...this.gridOptions.defaultColDef,
            valueFormatter(params) {
              return formatNumber(params.value);
            },
            cellStyle(params) {
              return alignCells(params);
            }
          },
        };
        this.paymentsGrid = {
          ..._.cloneDeep(this.gridOptions),
          rowGroupPanelShow: 'none',
          components: {
            'PaymentLink': LinkCellRenderer('payment_id', '/payments/')
          },
          defaultColDef: {
            ...this.gridOptions.defaultColDef,
            valueFormatter(params) {
              return formatNumber(params.value, 5);
            },
            cellStyle(params) {
              return alignForNumber(params.value);
            }
          },
          getRowStyle(params) {
            if (params.data.row_type === 'invoice') {
              return {'border-top': '2px solid #000', 'border-bottom': '2px solid #000', 'font-weight': 'bold'};
            }
            if (params.data.row_type === 'total') {
              return {'border-top': '2px solid #000', 'border-bottom': '1px solid #000', 'font-weight': 'bold'};
            }
            if (params.data.row_type === 'difference') {
              return {'border-top': '1px solid #000', 'border-bottom': '2px solid #000', 'font-weight': 'bold'};
            }
          },
        }
      },
      createRelatedInvoice(type) {
        const typeRelations = {
          'credit': CREDIT,
          'debit': DEBIT,
          'margin_correct': MARGIN_CORRECT,
        };
        const data = {
          buyer: this.invoiceData.buyer && this.invoiceData.buyer.id,
          partner_address: this.invoiceData.partner_address && this.invoiceData.partner_address.id,
          seller: this.invoiceData.seller && this.invoiceData.seller.id,
          currency: this.invoiceData.currency && this.invoiceData.currency.id,
          initial_invoice: this.invoiceId,
          type: typeRelations[type]
        };
        if (type !== 'margin_correct') {
          data.vat = this.invoiceData.vat && this.invoiceData.vat.id;
        }
        this.$http.post(`accounting/invoices/`, data)
          .then(handlerResponse)
          .then(async invoice => {
            await this.$router.push({path: `/sales-invoice/${invoice.id}`});
            this.invoiceData = {...this.invoiceData, ...invoice};
          }).catch(error => notificationsRenderer(error.data, 'error'));
      },
      createCompensationInvoice() {
        this.$root.$emit('openCreditNoteCompensationModal');
      },
      generateMR() {
        this.$http.get(`${this.commonUrl}margin_report/`, {
          responseType: 'blob'
        }).then((response, headers) => {
          const disposition = response.headers.get('content-disposition'),
            fileName = getFileNameFromContentDisposition(disposition, 'margin-report');
          saveAs(response.body, `${fileName}.xlsx`);
        }).catch(error => notificationsRenderer(error.body, 'error'));
      },
      generateCTB() {
        this.$http.get(`${this.commonUrl}ctb_certificate/`, {
          responseType: 'blob'
        }).then(async (response, headers) => {
          const disposition = response.headers.get('content-disposition'),
            fileName = getFileNameFromContentDisposition(disposition, 'ctb-certificate');
          await this.fetchDocuments()
          saveAs(response.body, `${fileName}.docx`);
        }).catch(error => notificationsRenderer(error.body, 'error'));
      },
      deleteSalesInvoice() {
        this.$swal.fire({
          title: this.$i18n.t(`dialogs.delete_invoice`),
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#1bc14d',
          cancelButtonColor: '#d33',
          confirmButtonText: this.$i18n.t(`common_content.yes`),
          cancelButtonText: this.$i18n.t(`common_content.no`)
        }).then(result => {
          if (result.value) {
            try {
              this.$http.delete(this.commonUrl)
                .then(() => {
                  this.$notify(setNotifyData(this.$i18n.t(`notifications.common.success`),
                    this.$i18n.t(`notifications.sales_invoice.invoice_deleted`), 'success'));
                  this.$router.push({path: '/sales-invoices'});
                });
            } catch (e) {
              this.$notify(setNotifyData('Error', e.data.detail, 'error'));
            }

          }
        });
      },
      async changeSalesInvoiceStatus(move_status, shouldSend=false) {
        const loader = this.$loading.show();
        try {
          const response = await this.$http.post(`${this.commonUrl}${move_status}/`, {
            shouldSend
          }).then(handlerResponse);
          loader.hide();
          await this.updateSalesInvoiceCb(response);
          this.currentTabIndex = 0;
        } catch (error) {
          loader.hide();
          this.currentTabIndex = 0;
          notificationsRenderer(error.data, 'error');
        }
      },
      updateSalesInvoiceCb(response) {
        this.$nextTick(() => {
          this.invoiceData = {...this.invoiceData, ...response};
          this.$notify(setNotifyData(this.$i18n.t(`notifications.common.success`),
            this.$i18n.t(`notifications.sales_invoice.invoice_updated`), 'success'));
          this.fetchTabDataByName();
          this.loadDeliveryPartnerPlace();
        });
      },
      prepareSavedData() {
        return {
          additional_costs_sum: this.invoiceData.additional_costs_sum,
          additional_information: getArrayByProperty(this.invoiceData.additional_information, 'id'),
          additional_total: this.invoiceData.additional_total,
          buyer: this.invoiceData.buyer && this.invoiceData.buyer.id,
          correction_reason: this.invoiceData.correction_reason && this.invoiceData.correction_reason.id,
          currency: this.invoiceData.currency && this.invoiceData.currency.id,
          date_due: this.invoiceData.date_due && moment(this.invoiceData.date_due).format(DATE_FORMAT),
          delivery_method: this.invoiceData.delivery_method && this.invoiceData.delivery_method.id,
          incoterm: this.invoiceData.incoterm && this.invoiceData.incoterm.id,
          invoice_date: this.invoiceData.invoice_date && moment(this.invoiceData.invoice_date).format(DATE_FORMAT),
          lc: this.invoiceData.lc,
          notes: this.invoiceData.notes,
          paid_vat: this.invoiceData.paid_vat,
          partner_address: this.invoiceData.partner_address && this.invoiceData.partner_address.id,
          payment_term: this.invoiceData.payment_term && this.invoiceData.payment_term.id,
          receiver_of_the_load: this.invoiceData.receiver_of_the_load && this.invoiceData.receiver_of_the_load.id,
          seller: this.invoiceData.seller && this.invoiceData.seller.id,
          total_transport: this.invoiceData.total_transport,
          transport_no: this.invoiceData.transport_no,
          value: this.invoiceData.value,
          invoice_number: this.invoiceData.invoice_number,
          vat: this.invoiceData.vat && this.invoiceData.vat.id,
          taken_into_cost: this.invoiceData.taken_into_cost
        }
      },
      async generateDocs() {
        try {
          await this.$http.post(`${this.commonUrl}attach_documents/`).then(handlerResponse);
          await this.fetchDocuments();
        } catch (error) {
          notificationsRenderer(error.data, 'error');
          throw error.data;
        }
      },
      save() {
        const savedData = this.prepareSavedData();
        if (this.isNewInvoice) {
          const invoiceTypeRelations = {
            'partner_invoice': PARTNER,
            'equipment_invoice': EQUIPMENT,
            'credit_invoice': CREDIT,
            'debit_invoice': DEBIT,
          };
          savedData.type = invoiceTypeRelations[this.invoiceType];
          this.$http.post(`accounting/invoices/`, savedData).then(handlerResponse).then(async (response) => {
            this.invoiceData = {...this.invoiceData, ...response};
            await this.$router.push({path: `/sales-invoice/${response.id}`});
            await this.fetchInvoiceData(true);
            this.$notify(setNotifyData(this.$i18n.t(`notifications.common.success`),
              this.$i18n.t(`notifications.sales_invoice.invoice_created`), 'success'));
          }).catch(error => notificationsRenderer(error.data, 'error'));
        } else {
          this.updateSalesInvoice(savedData);
        }
      },
      async updateSalesInvoice(data) {
        try {
          const response = await this.$http.put(this.commonUrl, data).then(handlerResponse);
          this.updateSalesInvoiceCb(response);
          await this.fetchDocuments();
        } catch (error) {
          notificationsRenderer(error.data, 'error');
          throw error.data;
        }
      },
      countSelectedRows() {
        this.selectedRowsCount = getArrayByProperty(this.commissionFeesGrid.api.getSelectedRows(), 'id') ?
          getArrayByProperty(this.commissionFeesGrid.api.getSelectedRows(), 'id').length : 0
      },
      countSelectedInvoicePackages() {
        const parentNodes = this.invoicePackageGrid.api.getSelectedNodes().map(node => node.parent);
        const groupNodes = _.uniq(getArrayByProperty(parentNodes, 'id')).filter(item => Number(item) < 0);
        this.invoiceItemsPackagesCount = groupNodes ? groupNodes.length : 0;
        const selectedPackagesCount = getFilteredNodes(this.invoicePackageGrid)
          .filter(node => !node.group && node.selected)
          .map(node => node.data);
        this.invoicePackagesCount = selectedPackagesCount.length;
      },
      countSelectedInvoiceItems() {
        const selectedInvoiceItemsCount = getFilteredNodes(this.invoiceItemGrid)
          .filter(node => node.selected)
          .map(node => node.data);
        this.invoiceItemsCount = selectedInvoiceItemsCount.length;
      },
      openAddCommissionFeeModal() {
        this.$root.$emit('openSalesInvoiceCommissionFeeModal');
      },
      openEditCommissionFeeModal(params) {
        if (this.currentUserCanEditInvoiceAgentFees) {
          this.$http.get(`accounting/sale_agent_commission_fees/${params.data.id}/`).then(handlerResponse)
            .then((response) => {
              this.$root.$emit('openSalesInvoiceCommissionFeeModal', {...response});
            });
        }
      },
      async deleteCommissionFee() {
        if (this.commissionFeesGrid.api.getSelectedRows().length) {
          try {
            const ids = getArrayByProperty(this.commissionFeesGrid.api.getSelectedRows(), 'id');
            const notify = await this.$http.delete('accounting/sale_agent_commission_fees/', {body: {ids}})
            this.$notify(setNotifyData('Success', notify.body, 'success'));
            await this.fetchCommissionFeesCallback();
          } catch (error) {
            notificationsRenderer(error.body, 'error');
          }
        } else {
          notificationsRenderer('Please select commission fees', 'error');
        }
      },
      async fetchCommissionFeesCallback() {
        await this.fetchCommissionFees();
        await this.fetchInvoiceData();
        this.currentTabName = 'commission-fees';
      },
      excelExport() {
        const params = {
          columnGroups: true,
          fileName: 'Payments Export'
        };
        this.paymentsGrid.api.exportDataAsExcel(params);
      },
      openChargeOffModal() {
        this.$root.$emit('openPaymentsChargeOffModal');
      },
      async cancelChargeOff() {
        await this.$http.post(`${this.commonUrl}cancel_charge_off/`);
        await this.fetchInvoiceData();
        await this.fetchPayments();
        this.currentTabName = 'payments';
      },
      editInvoiceItem(params) {
        if (this.currentUserCanEditInvoice) {
          if (!params.node.allLeafChildren && !this.isApprovedInvoice && !this.isDiscountType && !this.isCommissionType
            && !this.isGeneralType) {
            this.$root.$emit('openEditInvoiceItemModal', params.data);
          } else if (params.node.allLeafChildren && !this.isApprovedInvoice && !this.isDiscountType) {
            const parentData = params.node.allLeafChildren[0];
            this.$root.$emit('openEditInvoiceItemModal', parentData.data);
          }
        }
      },
      addInvoiceItems() {
        this.$root.$emit('openEditInvoiceItemModal');
      },
      addPackages() {
        this.$root.$emit('openAddPackagesModal');
      },
      async fetchInvoiceItemsCallback() {
        await this.fetchInvoiceItems();
        await this.fetchInvoiceData();
        this.currentTabName = 'invoice-items';
      },
      deleteInvoiceItems() {
        const ids = getArrayByProperty(this.invoiceItemGrid.api.getSelectedRows(), 'id');
        const invoicesNr = getArrayByProperty(this.invoiceItemGrid.api.getSelectedRows(), 'name');
        this.$swal.fire({
          title: this.$i18n.t(`alerts.delete_invoice_items`, {invoicesNr}),
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#1bc14d',
          cancelButtonColor: '#d33',
          confirmButtonText: this.$i18n.t(`common_content.yes`),
          cancelButtonText: this.$i18n.t(`common_content.no`)
        }).then(result => {
          if (result.value) {
            this.$http.delete(`${this.commonUrl}invoice_items/`, {body: {ids: ids}}).then(async () => {
              this.fetchInvoiceItemsCallback();
            }).catch(error => notificationsRenderer(error.data, 'error'));
          }
        });
      },
      async fetchInvoicePackagesCallback() {
        await this.fetchInvoicePackages();
        await this.fetchInvoiceData();
        this.currentTabName = 'invoice-packages';
      },
      deletePackages() {
        const ids = getArrayByProperty(this.invoicePackageGrid.api.getSelectedRows(), 'id');
        const packagesNr = getArrayByProperty(this.invoicePackageGrid.api.getSelectedRows(), 'package_nr');
        this.$swal.fire({
          title: this.$i18n.t(`alerts.delete_packages`, {packagesNr}),
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#1bc14d',
          cancelButtonColor: '#d33',
          confirmButtonText: this.$i18n.t(`common_content.yes`),
          cancelButtonText: this.$i18n.t(`common_content.no`)
        }).then(result => {
          if (result.value) {
            this.$http.delete(`${this.commonUrl}packages/`, {body: {packages_id: ids}}).then(async () => {
              await this.fetchInvoicePackages();
              await this.fetchInvoiceData();
              this.currentTabName = 'invoice-packages';
            }).catch(error => notificationsRenderer(error.data, 'error'));
          }
        });
      },
      async addPackagesToInvoice(ids) {
        const loader = this.$loading.show();
        await this.$http.put(`${this.commonUrl}packages/`, {packages_id: ids})
          .then(async () => {
            await this.fetchInvoicePackages();
            await this.fetchInvoiceData();
            loader.hide();
            this.currentTabName = 'invoice-packages';
          }).catch(error => notificationsRenderer(error.data, 'error'));
      },
      paymentsChargeOffToInvoice(data) {
        this.$http.post(`${this.commonUrl}charge_off_balance/`, data)
          .then(async () => {
            await this.fetchInvoiceData();
            await this.fetchPayments();
            this.currentTabName = 'payments';
          }).catch(error => notificationsRenderer(error.data, 'error'));
      },
      async setDefaultData() {
        const defaultVat = 0;
        this.invoiceData.vat = this.invoiceVats.find(vat => vat.value === defaultVat);
        this.invoiceData.invoice_date = moment(new Date()).format(DATE_FORMAT);
      }
    },
    async beforeRouteUpdate(to, from, next) {
      next();
      if (to.params.id === 'new') {
        cleanFilters(this.invoiceData);
        this.setDefaultData();
      } else {
        this.popoverCreateRelatedInvoice = false;
        await this.fetchInvoiceData(true);
      }
    },
    async mounted() {
      this.$store.cache.dispatch('woodware/getPartners', {is_active: true});
      this.$store.cache.dispatch('woodware/getIncoterms');
      this.$store.cache.dispatch('woodware/getPaymentTerms');
      this.$store.cache.dispatch('woodware/getDeliveryMethods');
      this.$store.cache.dispatch('woodware/getDeliveryAddresses');
      this.$store.cache.dispatch('generic/getCurrencies');
      this.$store.cache.dispatch('woodware/loadTextNotes');
      this.$store.cache.dispatch('woodware/loadCorrectionReasons');
      await this.$store.cache.dispatch('generic/loadVats');
      translateColumns.call(this, this.paymentsGridColumns);
      this.gridsInitialize();
      if (!this.isNewInvoice) {
        await this.fetchInvoiceData(true);
      } else {
        this.setDefaultData();
      }
    },
  }
</script>

<style lang="scss">
  .swal2-popup .swal2-title {
    display: block !important;
  }

  .vat-sum-text {
    top: 0;
    right: 0;
  }

  .vat-wrapper {
    height: 53px;
    padding-top: 25px;

    .vat-info {
      font-size: 11px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/modules/sales/invoices/SalesInvoice.vue